import React, { useMemo } from 'react';
import { makeCSS } from '../../utils/makeCSS';
import { SizeVariants } from '../../theme/themes';
import { Avatar } from '../atoms/Avatar';
import { cx } from '@emotion/css';
import { Overflow } from '../atoms/Overflow';
import { HBox, VBox } from '../templates/layout/Flex';
import { Typography } from '../atoms/Typography';
import AccountSuggestionItem from './SuggestionItems/AccountSuggestionItem';

export type UserInitial = {
  email_address?: string;
  full_name: string;
  profile_picture?: string;
};

/**
 * Displays a group of different Avatar components together.
 *  If the list is longer than the max amount of Avatars we want to show, items are omitted and a counter is displayed.
 */
export interface AvatarGroupProps {
  /**List of users initials */
  users?: UserInitial[];
  /**The displaying size*/
  size?: SizeVariants.xs | SizeVariants.sm | SizeVariants.md | SizeVariants.lg;
  /**Maximim mumber of Avatars*/
  maxCount?: number;
}

const useCSS = makeCSS(({ theme }) => {
  return {
    root: {
      display: 'inline-flex',
      alignItems: 'center',
      boxSizing: 'border-box'
    },
    'size-xs': { marginLeft: -2 },
    'size-sm': { marginLeft: -4 },
    'size-md': { marginLeft: -6 },
    'size-lg': { marginLeft: -8 },
    leftUsers: {
      position: 'relative',
      zIndex: 9999
    }
  };
});

export const AvatarGroup = (props: AvatarGroupProps) => {
  const { users, size = SizeVariants.md, maxCount } = props;

  const classes = useCSS();

  const list = useMemo(() => {
    return {
      toShow: users?.slice(0, maxCount),
      toHide: users?.slice(maxCount)
    };
  }, [users, maxCount]);
  const leftUsers = list.toHide.length;

  return (
    <div className={`${classes.root}`}>
      {list.toShow.map((user) => (
        <div className={cx(classes[`size-${size}`])} key={JSON.stringify(user)}>
          <Avatar
            userInitial={user.full_name || user.email_address}
            size={SizeVariants[size]}
            hideTooltip={!(user.full_name && user.email_address)}
            tooltip={user.full_name ? user.email_address : user.full_name}
            border={true}
            imageUrl={user.profile_picture}
          />
        </div>
      ))}
      {leftUsers > 0 && (
        <div className={cx(classes.leftUsers, classes[`size-${size}`])}>
          <Overflow
            number={leftUsers}
            category="avatar"
            size={SizeVariants[size]}
            tooltip={
              <VBox gap={4} justify="start">
                {list.toHide.map((li) => (
                  <AccountSuggestionItem
                    key={JSON.stringify(li)}
                    item={li}
                    size={SizeVariants.sm}
                  />
                ))}
              </VBox>
            }
          />
        </div>
      )}
    </div>
  );
};
